import React from 'react';
import { motion } from 'framer-motion';

const milestones = [
  {
    date: 'Spring 2025',
    quarter: 'Q2 2025',
    title: 'Stealth development begins',
    description: 'Core team assembled. Architecture designed. R&D phase for proprietary settlement engine and multi-rail payment infrastructure.',
    status: 'completed',
  },
  {
    date: 'Summer 2025',
    quarter: 'Q3 2025',
    title: 'Alpha testing & partnerships',
    description: 'Internal alpha launched. First banking and payment network partnerships established. QR payment integration testing across 5 initial markets.',
    status: 'completed',
  },
  {
    date: 'End of 2025',
    quarter: 'Q4 2025',
    title: 'MVP launch & cloud deployment',
    description: 'Public MVP released. Cloud infrastructure deployed for global scalability. First users onboarded. SWIFT/SEPA transfer capabilities go live.',
    status: 'completed',
  },
  {
    date: 'Early 2026',
    quarter: 'Q1 2026',
    title: 'Compliance & market integrations',
    description: 'Regulatory compliance processes initiated across key markets. Integration demos with major financial partners. QR payments expanded to 18 countries.',
    status: 'current',
  },
  {
    date: 'Mid 2026',
    quarter: 'Q2–Q3 2026',
    title: 'Platform expansion',
    description: 'Full iOS and Android apps launched. Business accounts and API platform for B2B integrations. Multi-currency balance support expanded.',
    status: 'future',
  },
  {
    date: '2027',
    quarter: '2027',
    title: 'Global scale',
    description: 'QR payment network expanded to 40+ countries. Licensed operations in major financial centers. Enterprise solutions and white-label offerings launched.',
    status: 'future',
  },
  {
    date: '2028+',
    quarter: '2028+',
    title: 'Market leadership',
    description: 'Become the leading cross-border payment platform. Launch embedded finance APIs, lending products, and become a category-defining fintech brand.',
    status: 'future',
  },
];

export const Roadmap = () => {
  return (
    <section id="roadmap" className="py-16 sm:py-20" style={{ background: '#F7F7FB' }} data-testid="roadmap-section">
      <div className="container-main">
        <div className="text-center mb-12">
          <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.18em] text-[#7C3AED] mb-3">
            Roadmap
          </p>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B0B10] mb-4">
            Our journey to global payments
          </h2>
          <p className="text-base text-[#64647A] max-w-lg mx-auto">
            From stealth development to market leadership — a clear path forward.
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-5 top-0 bottom-0 w-0.5" style={{ background: '#E7E7EF' }} />

            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                data-testid={`roadmap-milestone-${milestone.quarter.replace(/[\s+]/g, '-').toLowerCase()}`}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="relative flex gap-6 mb-8 last:mb-0"
              >
                {/* Node */}
                <div className="relative z-10 flex-shrink-0 mt-1">
                  <div
                    className={`h-[10px] w-[10px] rounded-full ${
                      milestone.status === 'completed'
                        ? 'bg-[#7C3AED]'
                        : milestone.status === 'current'
                        ? 'bg-[#7C3AED] ring-4 ring-[#EDE9FE]'
                        : 'bg-[#DDD6FE]'
                    }`}
                    style={{ marginLeft: '15px' }}
                  />
                </div>

                {/* Content card */}
                <div
                  className={`flex-1 rounded-2xl p-5 sm:p-6 ${
                    milestone.status === 'current'
                      ? 'bg-white border-2 border-[#7C3AED] shadow-sm'
                      : milestone.status === 'completed'
                      ? 'bg-white border border-[#E7E7EF] shadow-sm'
                      : 'bg-white border border-dashed border-[#DDD6FE]'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono-accent text-xs font-medium text-[#7C3AED]">{milestone.quarter}</span>
                    {milestone.status === 'current' && (
                      <span className="inline-flex items-center rounded-full bg-[#F5F3FF] text-[#7C3AED] text-xs px-2 py-0.5 font-medium">
                        Current phase
                      </span>
                    )}
                    {milestone.status === 'completed' && (
                      <span className="inline-flex items-center rounded-full bg-[#F0FDF4] text-[#16A34A] text-xs px-2 py-0.5 font-medium">
                        Completed
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-semibold text-[#0B0B10] mb-1">{milestone.title}</h3>
                  <p className="text-sm text-[#64647A] leading-relaxed">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
